import { cva } from "cva";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AriaRangeCalendarProps, I18nProvider } from "react-aria";
import {
  Calendar as AriaCalendar,
  CalendarGridHeader as AriaCalendarGridHeader,
  type CalendarProps as AriaCalendarProps,
  RangeCalendar as AriaRangeCalendar,
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarHeaderCell,
  type DateValue,
  Heading,
  Text,
  useLocale,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "./utils";

const cellStyles = cva({
  base: "size-10 text-sm text-theme-on-background cursor-default rounded-full flex items-center justify-center forced-color-adjust-none m-0.5",
  variants: {
    isSelected: {
      false: "",
      true: "border-2 border-theme-primary invalid:text-red-600 invalid:border-red-600 forced-colors:bg-[Highlight] forced-colors:invalid:bg-[Mark] forced-colors:text-[HighlightText]",
    },
    isFocused: {
      true: "bg-theme-on-background-hover",
    },
    isDisabled: {
      true: "text-theme-on-background-variant forced-colors:text-[GrayText]",
    },
    isUnavailable: {
      true: "text-theme-on-background-variant forced-colors:text-[GrayText]",
    },
  },
  compoundVariants: [
    {
      isDisabled: false,
      isUnavailable: false,
      className: "cursor-pointer hover:bg-theme-on-background-hover",
    },
  ],
});

export interface CalendarProps<T extends DateValue>
  extends Omit<AriaCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

export function Calendar<T extends DateValue>({
  errorMessage,
  ...props
}: CalendarProps<T>) {
  return (
    <I18nProvider locale="da-DK">
      <AriaCalendar {...props}>
        <CalendarHeader />
        <CalendarGrid>
          <CalendarGridHeader />
          <CalendarGridBody>
            {(date) => (
              <CalendarCell
                date={date}
                className={(renderProps) =>
                  twMerge(focusRing(renderProps), cellStyles(renderProps))
                }
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
        {errorMessage && (
          <Text slot="errorMessage" className="text-sm text-red-600">
            {errorMessage}
          </Text>
        )}
      </AriaCalendar>
    </I18nProvider>
  );
}

export function CalendarHeader() {
  const { direction } = useLocale();

  return (
    <header className="flex w-full items-center gap-1 px-1 pb-4">
      <Button
        className="bg-theme-on-background-hover flex cursor-pointer items-center justify-center rounded-full p-1 disabled:opacity-20"
        slot="previous"
      >
        {direction === "rtl" ? (
          <ChevronLeft size={24} className="text-theme-active" aria-hidden />
        ) : (
          <ChevronLeft size={24} className="text-theme-active" aria-hidden />
        )}
      </Button>

      <Heading className="text-theme-on-background mx-2 flex-1 text-center text-sm font-bold text-wrap uppercase" />

      <Button
        className="bg-theme-on-background-hover flex cursor-pointer items-center justify-center rounded-full p-1 disabled:opacity-20"
        slot="next"
      >
        {direction === "rtl" ? (
          <ChevronLeft size={24} className="text-theme-active" aria-hidden />
        ) : (
          <ChevronRight size={24} className="text-theme-active" aria-hidden />
        )}
      </Button>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="text-theme-active text-xs font-semibold">
          {day}
        </CalendarHeaderCell>
      )}
    </AriaCalendarGridHeader>
  );
}

const cellRangeStyles = cva({
  base: "size-10 text-sm text-theme-on-background p-4 cursor-default rounded-full flex items-center justify-center forced-color-adjust-none m-1",
  variants: {
    isSelected: {
      false: "hover:bg-theme-on-background-hover",
      true: "border-theme-primary border-2 forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]",
    },
    isRangeSelection: {
      true: "bg-theme-primary/20 rounded-none first:rounded-l-full last:rounded-r-full",
    },
    isRangeStart: {
      true: "border-theme-primary border bg-none rounded-full",
    },
    isRangeEnd: {
      true: "border-theme-primary border-2 rounded-full",
    },
    isDisabled: {
      true: "text-theme-on-background-variant forced-colors:text-[GrayText]",
      false: "cursor-pointer",
    },
  },
});

export interface RangeCalendarProps<T extends DateValue>
  extends Omit<AriaRangeCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

export function RangeCalendar<T extends DateValue>({
  errorMessage,
  ...props
}: RangeCalendarProps<T>) {
  return (
    <I18nProvider locale="da-DK">
      <AriaRangeCalendar {...props}>
        <RangeCalendarHeader />
        <CalendarGrid>
          <CalendarGridHeader />
          <CalendarGridBody>
            {(date) => (
              <CalendarCell
                date={date}
                className={({
                  isSelected,
                  isSelectionStart,
                  isSelectionEnd,
                  isDisabled,
                }) =>
                  cellRangeStyles({
                    isSelected: isSelectionStart || isSelectionEnd,
                    isRangeSelection:
                      isSelected && !isSelectionStart && !isSelectionEnd,
                    isRangeStart: isSelectionStart,
                    isRangeEnd: isSelectionEnd,
                    isDisabled,
                  })
                }
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
        {errorMessage && (
          <Text slot="errorMessage" className="text-sm text-red-600">
            {errorMessage}
          </Text>
        )}
      </AriaRangeCalendar>
    </I18nProvider>
  );
}

export function RangeCalendarHeader() {
  return (
    <header className="flex w-full items-center gap-1 px-1 pb-4">
      <Button
        className="bg-theme-on-background-hover flex cursor-pointer items-center justify-center rounded-full p-1 disabled:opacity-20"
        slot="previous"
      >
        <ChevronLeft size={24} className="text-theme-active" aria-hidden />
      </Button>

      <Heading className="text-theme-on-background mx-2 flex-1 text-center text-sm font-bold text-wrap uppercase" />

      <Button
        className="bg-theme-on-background-hover flex cursor-pointer items-center justify-center rounded-full p-1 disabled:opacity-20"
        slot="next"
      >
        <ChevronRight size={24} className="text-theme-active" aria-hidden />
      </Button>
    </header>
  );
}
